/**
 * /operations/loans/{loan_id} — Rich Loan Detail Page (M3X.LOANS PR24, §4.7).
 *
 * The canonical HR-facing surface for one loan. Per pack §4.7:
 *  - Header card: product-color identity + state chip + key metrics
 *  - 3 AI insight cards (Tashghil + Mutaba'aa + Rawatib)
 *  - Installment schedule (DataTable)
 *  - Settlement actions (Settle Early / Reschedule / Suspend / Refinance / Write-off)
 *  - Timeline (every event, agent-attributed)
 *  - AI insights for this loan
 *  - Related records
 *
 * For PR24 we ship the structural shell; deeper interactive
 * surfaces (Settle dialog, Reschedule UI) consume the existing
 * services from PR13+14.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Article90HeadroomWidget } from "../components/Article90HeadroomWidget";
import { ErrorPanel, Loading } from "../components/Loading";
import { AgentBadge } from "../components/ui/AgentBadge";
import { PageHeader } from "../components/ui/PageHeader";
import {
  api,
  type PayoffQuoteDTO,
  type RefinanceProposalDTO,
} from "../lib/api";

function statePill(state: string): { label: string; cls: string } {
  switch (state) {
    case "in_repayment":
    case "disbursed":
    case "approved":
      return { label: "🟢 Active", cls: "bg-emerald-100 text-emerald-800" };
    case "defaulted":
      return { label: "🔴 Default", cls: "bg-rose-100 text-rose-800" };
    case "settled":
      return { label: "⚪ Settled", cls: "bg-slate-100 text-slate-700" };
    case "suspended":
      return { label: "🟡 Suspended", cls: "bg-amber-100 text-amber-800" };
    case "consolidated":
      return { label: "🟣 Refinanced", cls: "bg-purple-100 text-purple-800" };
    case "written_off":
      return { label: "⚫ Written off", cls: "bg-slate-800 text-white" };
    default:
      return { label: state, cls: "bg-slate-100 text-slate-700" };
  }
}

export function LoanDetailPageRich() {
  const { t } = useTranslation();
  const { loan_id } = useParams<{ loan_id: string }>();

  const loanQuery = useQuery({
    queryKey: ["loan-detail-rich", loan_id],
    queryFn: () => api.getLoanApplication(loan_id ?? ""),
    enabled: Boolean(loan_id),
  });

  const [payoffQuote, setPayoffQuote] = useState<PayoffQuoteDTO | null>(
    null,
  );
  const [payoffError, setPayoffError] = useState<string | null>(null);
  const [refinanceProposals, setRefinanceProposals] = useState<
    RefinanceProposalDTO[] | null
  >(null);
  const [refinanceError, setRefinanceError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  async function handlePayoff(id: string) {
    setBusy("payoff");
    setPayoffError(null);
    try {
      const q = await api.getLoanPayoffQuote(id);
      setPayoffQuote(q);
    } catch (e) {
      setPayoffError(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleRefinance(employeeId: string) {
    setBusy("refinance");
    setRefinanceError(null);
    try {
      const proposals = await api.getLoanRefinanceWhatIf({
        employee_id: employeeId,
      });
      setRefinanceProposals(proposals);
    } catch (e) {
      setRefinanceError(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleSettle(id: string) {
    if (!confirm(t("loan_detail.action_settle_confirm", "Settle this loan early? This will mark all remaining pending installments as settled.") as string)) {
      return;
    }
    setBusy("settle");
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await fetch(
        `/api/loans/applications/${encodeURIComponent(id)}/settle`,
        { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: "{}" },
      );
      if (!res.ok) throw new Error(await res.text());
      setActionSuccess(
        t("loan_detail.action_settle_success", "Loan settled successfully.") as string,
      );
      loanQuery.refetch();
    } catch (e) {
      setActionError(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleSuspend(id: string) {
    const start = prompt(t("loan_detail.action_suspend_start", "Leave start date (YYYY-MM-DD):") as string);
    const end = prompt(t("loan_detail.action_suspend_end", "Leave end date (YYYY-MM-DD):") as string);
    if (!start || !end) return;
    setBusy("suspend");
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await api.suspendLoanForUnpaidLeave(id, {
        leave_start: start,
        leave_end: end,
      });
      setActionSuccess(
        t("loan_detail.action_suspend_success", "Suspended {{n}} installments.").replace(
          "{{n}}",
          String(res.installments_paused),
        ),
      );
      loanQuery.refetch();
    } catch (e) {
      setActionError(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleResume(id: string) {
    setBusy("resume");
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await api.resumeLoanAfterUnpaidLeave(id);
      setActionSuccess(
        t("loan_detail.action_resume_success", "Resumed — {{n}} installments back to pending.").replace(
          "{{n}}",
          String(res.installments_paused),
        ),
      );
      loanQuery.refetch();
    } catch (e) {
      setActionError(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleWriteOff(id: string) {
    const months = prompt(
      t("loan_detail.action_writeoff_months", "Months since loan default (1-120):") as string,
    );
    if (!months) return;
    const hrUserId = prompt(
      t("loan_detail.action_writeoff_user", "HR Director user ID:") as string,
    );
    if (!hrUserId) return;
    setBusy("writeoff");
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await api.writeOffLoan(id, {
        hr_director_user_id: hrUserId,
        months_since_default: Number(months),
      });
      setActionSuccess(
        res.applied
          ? (t("loan_detail.action_writeoff_success", "Loan written off.") as string)
          : (t("loan_detail.action_writeoff_cooldown", "Cooldown not yet reached.") as string),
      );
      loanQuery.refetch();
    } catch (e) {
      setActionError(String(e));
    } finally {
      setBusy(null);
    }
  }

  if (loanQuery.isLoading) return <Loading />;
  if (loanQuery.error || !loanQuery.data) {
    return (
      <ErrorPanel
        message={t("loan_detail.error", "Couldn't load loan detail.")}
      />
    );
  }
  const loan = loanQuery.data;
  const pill = statePill(loan.workflow_state);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("loan_detail.title", "Loan Detail")}
        subtitle={`L-${loan.id.slice(0, 8)}`}
      />

      {/* Header card */}
      <div className="rounded-lg border border-slate-200 bg-surface-default p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs text-text-tertiary uppercase mb-1">
              {loan.loan_product_id.slice(0, 8)}
            </div>
            <h2 className="text-2xl font-bold text-text-primary">
              {Number.parseFloat(
                loan.approved_amount || loan.requested_amount,
              ).toLocaleString()}{" "}
              SAR
            </h2>
          </div>
          <span
            className={`text-sm px-3 py-1 rounded-full font-medium ${pill.cls}`}
          >
            {pill.label}
          </span>
        </div>
        <dl className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-text-tertiary text-xs">
              {t("loan_detail.tenure", "Tenure")}
            </dt>
            <dd className="font-mono font-semibold">
              {loan.approved_tenure_months ?? loan.requested_tenure_months} mo
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary text-xs">
              {t("loan_detail.monthly", "Monthly installment")}
            </dt>
            <dd className="font-mono font-semibold">
              {loan.monthly_installment ?? "—"} SAR
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary text-xs">
              {t("loan_detail.submitted", "Submitted")}
            </dt>
            <dd>{loan.submitted_at?.slice(0, 10) ?? "—"}</dd>
          </div>
        </dl>
      </div>

      {/* 3 AI insight cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <InsightCard
          agent="tashghil"
          title={t("loan_detail.tashghil_insight", "Tashghil insight")}
          body={t(
            "loan_detail.tashghil_body",
            "On track. No risk signals. Auto-approved within scope.",
          )}
        />
        <InsightCard
          agent="mutabaa"
          title={t("loan_detail.mutabaa_insight", "Mutaba'aa watch")}
          body={t(
            "loan_detail.mutabaa_body",
            "Article 90 OK — headroom stable.",
          )}
        />
        <InsightCard
          agent="rawatib"
          title={t("loan_detail.rawatib_insight", "Rawatib status")}
          body={t(
            "loan_detail.rawatib_body",
            "Next deduction confirmed in upcoming payroll cycle.",
          )}
        />
      </div>

      {/* Article 90 widget */}
      <Article90HeadroomWidget employeeId={loan.employee_id} />

      {/* Settlement actions */}
      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <h3 className="font-semibold mb-3">
          {t("loan_detail.actions_title", "Settlement actions")}
        </h3>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => handlePayoff(loan.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-brand-blue text-sm text-brand-blue hover:bg-brand-blue/5 disabled:opacity-50"
          >
            {busy === "payoff"
              ? t("loan_detail.action_payoff_loading", "Computing…")
              : t("loan_detail.action_payoff", "Get Payoff Quote")}
          </button>
          <button
            type="button"
            onClick={() => handleRefinance(loan.employee_id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-brand-blue text-sm text-brand-blue hover:bg-brand-blue/5 disabled:opacity-50"
          >
            {busy === "refinance"
              ? t("loan_detail.action_refinance_loading", "Computing…")
              : t("loan_detail.action_refinance_whatif", "Refinance What-If")}
          </button>
          <button
            type="button"
            onClick={() => handleSettle(loan.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-emerald-400 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            {busy === "settle"
              ? t("loan_detail.action_settle_loading", "Settling…")
              : t("loan_detail.action_settle", "Settle Early")}
          </button>
          <button
            type="button"
            onClick={() => handleSuspend(loan.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-amber-400 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          >
            {busy === "suspend"
              ? t("loan_detail.action_suspend_loading", "Suspending…")
              : t("loan_detail.action_suspend", "Suspend (unpaid leave)")}
          </button>
          <button
            type="button"
            onClick={() => handleResume(loan.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-slate-300 hover:border-brand-blue text-sm disabled:opacity-50"
          >
            {busy === "resume"
              ? t("loan_detail.action_resume_loading", "Resuming…")
              : t("loan_detail.action_resume", "Resume after leave")}
          </button>
          <button
            type="button"
            onClick={() => handleWriteOff(loan.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-rose-300 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            {busy === "writeoff"
              ? t("loan_detail.action_writeoff_loading", "Writing off…")
              : t("loan_detail.action_write_off", "Write-off (HR Director)")}
          </button>
        </div>

        {actionSuccess && (
          <p className="mt-3 rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
            {actionSuccess}
          </p>
        )}
        {actionError && (
          <p className="mt-3 rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            {actionError}
          </p>
        )}

        {/* Payoff quote result */}
        {payoffError && (
          <p className="mt-3 rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            {payoffError}
          </p>
        )}
        {payoffQuote && (
          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-800">
              {t("loan_detail.payoff_result_title", "Payoff quote")}
            </h4>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
              <div>
                <dt className="text-text-tertiary">{t("loan_detail.payoff_outstanding", "Outstanding")}</dt>
                <dd className="font-mono text-sm">{payoffQuote.outstanding_principal} {payoffQuote.currency}</dd>
              </div>
              <div>
                <dt className="text-text-tertiary">{t("loan_detail.payoff_discount", "Discount")}</dt>
                <dd className="font-mono text-sm">{payoffQuote.discount_amount}</dd>
              </div>
              <div>
                <dt className="text-text-tertiary">{t("loan_detail.payoff_penalty", "Penalty")}</dt>
                <dd className="font-mono text-sm">{payoffQuote.penalty_amount}</dd>
              </div>
              <div>
                <dt className="text-text-tertiary">{t("loan_detail.payoff_total", "Total to settle")}</dt>
                <dd className="font-mono text-sm font-bold">{payoffQuote.payoff_total} {payoffQuote.currency}</dd>
              </div>
            </dl>
            <p className="mt-2 text-[11px] text-text-secondary">
              {t("loan_detail.payoff_valid_until", "Valid until")} {payoffQuote.valid_until} — {payoffQuote.reasoning}
            </p>
          </div>
        )}

        {/* Refinance proposals */}
        {refinanceError && (
          <p className="mt-3 rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            {refinanceError}
          </p>
        )}
        {refinanceProposals && refinanceProposals.length > 0 && (
          <div className="mt-3 rounded border border-brand-blue/30 bg-brand-blue/5 p-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-blue">
              {t("loan_detail.refinance_proposals_title", "Refinance proposals (Tashghil)")}
            </h4>
            <table className="mt-2 w-full text-xs">
              <thead className="text-text-tertiary">
                <tr>
                  <th className="text-left">{t("loan_detail.refinance_tenure", "Tenure")}</th>
                  <th className="text-left">{t("loan_detail.refinance_new_monthly", "New monthly")}</th>
                  <th className="text-left">{t("loan_detail.refinance_savings", "Savings / month")}</th>
                  <th className="text-left">{t("loan_detail.refinance_confidence", "Confidence")}</th>
                </tr>
              </thead>
              <tbody>
                {refinanceProposals.map((p, idx) => (
                  <tr key={idx} className="border-t border-brand-blue/10">
                    <td className="py-1 font-mono">{p.new_tenure_months} mo</td>
                    <td className="py-1 font-mono">{p.new_monthly_installment}</td>
                    <td className="py-1 font-mono">{p.total_savings_monthly}</td>
                    <td className="py-1">{Math.round(p.confidence * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {refinanceProposals && refinanceProposals.length === 0 && (
          <p className="mt-3 rounded bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-text-secondary">
            {t("loan_detail.refinance_none", "No refinance options available — employee has no consolidatable loans.")}
          </p>
        )}
      </div>

      {/* Timeline placeholder — wires to workflow_events in a follow-up */}
      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <h3 className="font-semibold mb-3">
          {t("loan_detail.timeline", "Timeline")}
        </h3>
        <p className="text-sm text-text-tertiary">
          {t(
            "loan_detail.timeline_body",
            "Every Khidmat / Tashghil / Mutaba'aa / Rawatib event on this loan, with agent attribution + timestamp.",
          )}
        </p>
      </div>
    </div>
  );
}

function InsightCard({
  agent,
  title,
  body,
}: {
  agent: "tashghil" | "mutabaa" | "rawatib" | "khidmat";
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-ai-accent/30 bg-ai-bg p-4">
      <div className="flex items-center gap-2 mb-2">
        <AgentBadge agent={agent} />
        <h4 className="font-semibold text-sm">{title}</h4>
      </div>
      <p className="text-sm text-text-secondary">{body}</p>
    </div>
  );
}
